// src/services/whatsapp.service.ts
//
// WhatsApp sharing for a secret link — 100% client-side, no API.
//
// There is no server-side/provider integration by design: `sendWhatsAppLink`
// builds a `wa.me` deep link and the UI opens it, launching the user's OWN
// WhatsApp (app or web) with the destination number and the secret link
// pre-filled. The user taps "send" themselves. No provider, credentials, or
// backend involved.

export interface SendWhatsAppLinkParams {
  /** Dialing code, digits only, no leading "+" (e.g. "55" for Brazil). */
  countryCode: string;
  /** Local phone number as typed by the user (may contain spaces/dashes). */
  number: string;
  /** The secret share URL to deliver. */
  secretUrl: string;
  /** Optional extra text placed before the link. */
  message?: string;
}

export interface WhatsAppSendResult {
  /**
   * The wa.me deep link to open, or undefined when there is no usable number.
   * The caller opens this so the user sends from their own WhatsApp.
   */
  waMeUrl?: string;
}

/** Keep only digits — WhatsApp's wa.me expects a bare international number. */
export function sanitizePhone(countryCode: string, number: string): string {
  const cc = (countryCode ?? '').replace(/\D/g, '');
  const local = (number ?? '').replace(/\D/g, '');
  return `${cc}${local}`;
}

/**
 * Build a wa.me deep link for a phone number + prefilled text.
 * Returns an empty string when there is no usable number.
 */
export function buildWaMeUrl(params: SendWhatsAppLinkParams): string {
  const phone = sanitizePhone(params.countryCode, params.number);
  if (!phone) return '';
  const text = params.message ? `${params.message} ${params.secretUrl}` : params.secretUrl;
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
}

/**
 * Build the wa.me deep link for the secret link. Returns `{ waMeUrl }` for the
 * caller to open (undefined when the number is missing). Never sends anything
 * on its own — the user sends from their own WhatsApp.
 */
export function sendWhatsAppLink(params: SendWhatsAppLinkParams): WhatsAppSendResult {
  const waMeUrl = buildWaMeUrl(params);
  return waMeUrl ? { waMeUrl } : {};
}
